/**
 * 공동인증서 PC에서 사용
 */
import { merge } from 'lodash';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'scbk-shared-module';
import { DelfinoSdkManager } from '../service/DelfinoSdkManager';
import { delfinoOptions, delfinoResult } from '../types/delfino';

declare var Delfino: any;
declare var DelfinoConfig: any;

/** 업무파라메터를 SDK 규격에 맞게 조립후 전송하는 훅  */
const useDelfino = () => {
  /** 한국어:KOR(default), 영어:ENG */
  const { i18n } = useTranslation();
  const lang = useRef<'KOR' | 'ENG' | 'CHN' | 'JPN' | 'VNM'>('KOR');
  useEffect(() => {
    if (i18n.language === 'en') {
      lang.current = 'ENG';
    } else {
      lang.current = 'KOR';
    }
  }, [i18n.language]);

  /** 모듈 기본 옵션 */
  const delfinoDefaultOptions: delfinoOptions = {
    withMACAddressAll: true,
    displayProviders: 'delfino',
    cacheCertFilter: false,
  };

  /** 콜백 래핑 */
  const defaultCB = (originCallback: (result?: delfinoResult) => void, result?: delfinoResult) => {
    console.log('result=>' + result);

    if (result?.status === 0) {
      console.log('❌사용자 닫음'); // 닫기버튼 무시
    } else if (result?.status === 1) {
      console.log('🟢Delfino 정상응답 받았습니다');
      originCallback(result); // 정상
    } else {
      Dialog.alert({ content: result?.message + '[' + result?.status + ']' });
    }
  };

  // const defaultFailCallback = () => {};

  return {
    /**
     * 인증서/본인확인 로그인
     * @param loginGB
     * @param complateCB
     * @param options
     */
    login: async (
      loginGB: 'cert' | 'vid',
      complateCB: (result?: delfinoResult) => void,
      options?: delfinoOptions,
    ) => {
      // sdk 확인
      await DelfinoSdkManager.getInstance().ensureReady(lang.current);

      if (loginGB === 'cert') {
        DelfinoSdkManager.getInstance().certLogin(
          (result?: delfinoResult) => {
            defaultCB(complateCB, result);
          },
          merge({}, delfinoDefaultOptions, options),
        );
      } else if (loginGB === 'vid') {
        DelfinoSdkManager.getInstance().vidLogin(
          (result?: delfinoResult) => {
            defaultCB(complateCB, result);
          },
          merge({}, delfinoDefaultOptions, options),
        );
      }
    },

    /**
     *
     * 1. dataType이 formattedText 일떄
     * ```
     * signData : "524902-01-055983:10,000:홍콩은행:097-21-0441-120:김모군"
     * confirmFormat : "출금계좌번호:이체금액:입금은행:입금계좌번호:받는분"
     * ```
     * 2. dataType이 strings 일때
     * ```
     * signData : "(1)거래일자: 2015.10.08\n (2)거래시간: 10:18:59\n"
     * ```
     */
    /*
    confirmSign: async (
      signData: string,
      complateCB: (result?: delfinoResult) => void,
      dataType: 'formattedText' | 'strings',
      confirmFormat?: string,
      options?: delfinoOptions,
    ) => {
      // sdk 확인
      await DelfinoSdkManager.getInstance().ensureReady(lang.current);
    },
    */

    /**
     * 일반 전자서명
     * @param signData
     * @param complateCB
     * @param options : delfinoOptions type 참고
     */
    sign: async (
      signData: string,
      complateCB: (result?: delfinoResult) => void,
      options?: delfinoOptions,
    ) => {
      // sdk 확인
      await DelfinoSdkManager.getInstance().ensureReady(lang.current);

      DelfinoSdkManager.getInstance().sign(
        signData,
        merge({}, delfinoDefaultOptions, options),
        (result?: delfinoResult) => {
          defaultCB(complateCB, result);
        },
      );
    },

    /**
     * 다중 전자서명
     * @param signData
     * @param signOptions
     * @param complateCB
     */
    multiSign: async (
      signData: string,
      signOptions: delfinoOptions,
      complateCB: (result?: delfinoResult) => void,
    ) => {
      // sdk 확인
      await DelfinoSdkManager.getInstance().ensureReady(lang.current);

      DelfinoSdkManager.getInstance().multiSign(
        signData,
        merge({}, delfinoDefaultOptions, signOptions),
        (result?: delfinoResult) => {
          defaultCB(complateCB, result);
        },
      );
    },

    /**
     * 인증서 내보내기
     * @param complateCB
     */
    exportCertificate: async (complateCB: (result?: delfinoResult) => void) => {
      // sdk 확인
      await DelfinoSdkManager.getInstance().ensureReady(lang.current);

      DelfinoSdkManager.getInstance().exportCertificate((result?: delfinoResult) => {
        defaultCB(complateCB, result);
      });
    },

    /**
     * 인증서 가져오기
     * @param complateCB
     */
    importCertificate: async (complateCB: (result?: delfinoResult) => void) => {
      // sdk 확인
      await DelfinoSdkManager.getInstance().ensureReady(lang.current);

      DelfinoSdkManager.getInstance().importCertificate((result?: delfinoResult) => {
        defaultCB(complateCB, result);
      });
    },

    /**
     * 인증서 발급
     * @param caName
     * @param caIp
     * @param caPort
     * @param referenceValue (참조번호)
     * @param secretValue (인가코드)
     * @param complateCB
     * @param recovery: true-재발급, false-신규발급
     */
    requestCertificate: async (
      caName: 'kica' | 'yessign' | 'yessignWebCmp',
      caIp: string,
      caPort: number,
      referenceValue: string,
      secretValue: string,
      complateCB: (result?: delfinoResult) => void,
      recovery: boolean,
    ) => {
      // sdk 확인
      await DelfinoSdkManager.getInstance().ensureReady(lang.current);

      // CA별 IP설정
      if (caName === 'kica') {
        DelfinoConfig.kicaCaHost = caIp;
        DelfinoConfig.kicaCaPort = caPort;
      } else if (caName === 'yessign' || caName === 'yessignWebCmp') {
        DelfinoConfig.yessignCaHost = caIp;
        DelfinoConfig.yessignCaPort = caPort;
      }

      // CA별 메소드 다르다
      if (caName === 'kica') {
        // 정보인증 발급
        DelfinoSdkManager.getInstance().requestCertificate2(
          caName,
          referenceValue,
          secretValue,
          (result?: delfinoResult) => {
            defaultCB(complateCB, result);
          },
          merge(
            {},
            {
              enableKmCert: true,
              recovery: recovery,
            },
          ),
        );
      } else if (caName === 'yessign' || caName === 'yessignWebCmp') {
        DelfinoSdkManager.getInstance().requestCertificate(
          caName,
          referenceValue,
          secretValue,
          (result?: delfinoResult) => {
            defaultCB(complateCB, result);
          },
          merge({}, { recovery: recovery }),
        );
      }
    },

    /**
     * 인증서 관리
     */
    manageCertificate: async () => {
      // sdk 확인
      await DelfinoSdkManager.getInstance().ensureReady(lang.current);
      DelfinoSdkManager.getInstance().manageCertificate();
    },

    /**
     * 인증서 삭제
     * @param serialNumber (10진수 시리얼번호)
     * @param complateCB
     */
    deleteCertificate: async (
      serialNumber: number,
      complateCB: (result?: delfinoResult) => void,
    ) => {
      // sdk 확인
      await DelfinoSdkManager.getInstance().ensureReady(lang.current);

      DelfinoSdkManager.getInstance().deleteCertificate(
        serialNumber,
        { key: 'serialNumberDec' }, // 10진수 시리얼번호로 필터링하여 삭제함
        (result?: delfinoResult) => {
          defaultCB(complateCB, result);
        },
      );
    },

    /**
     * 인증서 갱신
     * @param caName
     * @param caIp
     * @param caPort
     * @param serialNumber
     * @param complateCB
     */
    updateCertificate: async (
      caName: 'kica' | 'yessign' | 'yessignWebCmp',
      caIp: string,
      caPort: number,
      serialNumber: number,
      complateCB: (result?: delfinoResult) => void,
    ) => {
      // sdk 확인
      await DelfinoSdkManager.getInstance().ensureReady(lang.current);

      // 시리얼번호 설정
      const options = {
        serialNumberDecCertFilter: serialNumber + '',
        cacheCertFilter: false,
        cachCert: false,
        enableKmCert: false,
      };

      // CA별 IP설정
      if (caName === 'kica') {
        DelfinoConfig.kicaCaHost = caIp;
        DelfinoConfig.kicaCaPort = caPort;
        options.enableKmCert = true;
      } else if (caName === 'yessign' || caName === 'yessignWebCmp') {
        DelfinoConfig.yessignCaHost = caIp;
        DelfinoConfig.yessignCaPort = caPort;
      }

      DelfinoSdkManager.getInstance().updateCertificate2(
        caName,
        (result?: delfinoResult) => {
          defaultCB(complateCB, result);
        },
        merge({}, delfinoDefaultOptions, options),
      );
    },

    /**
     * 본인인증
     * @param provider
     * @param signData
     * @param complateCB
     * @param options
     */
    auth: async (
      provider: string,
      signData: string,
      complateCB: (result?: delfinoResult) => void,
      options?: delfinoOptions,
    ) => {
      // sdk 확인
      await DelfinoSdkManager.getInstance().ensureReady(lang.current);

      const authOptions = merge({}, options, { provider: provider });

      DelfinoSdkManager.getInstance().auth2(signData, authOptions, complateCB);
    },

    /**
     * G3 모듈 설치 체크
     */
    delfinoG3Install: async () => {
      // sdk 확인
      await DelfinoSdkManager.getInstance().ensureReady(lang.current);

      return new Promise<boolean>((resolve) => {
        DelfinoSdkManager.getInstance().isInstall((result) => {
          resolve(result);
        });
      });
    },

    /**
     * 갱신할 인증서 제출
     * @param loginGB
     * @param complateCB
     * @param options
     */
    login2: async (
      loginGB: 'cert',
      complateCB: (result?: delfinoResult) => void,
      certStore: string,
      options?: delfinoOptions,
    ) => {
      // sdk 확인
      await DelfinoSdkManager.getInstance().ensureReady(lang.current);
      if (loginGB === 'cert') {
        DelfinoSdkManager.getInstance().certLogin2(
          (result?: delfinoResult) => {
            defaultCB(complateCB, result);
          },
          certStore,
          merge({}, delfinoDefaultOptions, options),
        );
      }
    },
  }; // end-return;
};

export default useDelfino;
